import React, { Dispatch, SetStateAction, useState, useEffect, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { RecurringDatePicker } from '@charitystack/shared';
import MultiSelectDropdownNoCreate from '../../dropdowns/MultiSelectDropdownNoCreate';
import dayjs from 'dayjs';
import {
  getProductGroupsForFrequency,
  type FundraisingFormData,
  type Fund,
  type FundraiserData,
  type FrequencyType,
  type DayjsLike,
} from '../../drawers/types/FundraisingPaymentTypes';
import type { Merchant } from '../../../types/common';
import {
  formatOfflinePaymentMethod,
  OFFLINE_PAYMENT_METHODS,
} from '../../../utils/offlinePaymentMethods';

// Helper function to get the effective timezone
const getEffectiveTimezone = (merchant: Merchant | null): string => {
  return merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Props interface for FundraiserInformation component
interface FundraiserInformationProps {
  formData: FundraisingFormData;
  setFormData: Dispatch<SetStateAction<FundraisingFormData>>;
  merchant: Merchant | null;
  invalidFields: Set<string>;
  setInvalidFields: Dispatch<SetStateAction<Set<string>>>;
  handleFieldFocus: (fieldName: string) => void;
  isEditMode: boolean;
  isDrawerOpen: boolean;
  // Handlers passed from parent to avoid state reset on navigation
  paymentType: string;
  setPaymentType: Dispatch<SetStateAction<string>>;
  startValues: (Date | null)[];
  setStartValue: (index: number, value: Date | null) => void;
  endValues: (Date | string | null)[];
  setEndValue: (index: number, value: Date | string | null) => void;
  timeValues: (string | null)[];
  setTimeValue: (index: number, value: string | null) => void;
  handleFundChange: (index: number, field: string, value: string | number) => void;
  calculateTotalSubscriptionCharge: (
    index: number,
    startValue: DayjsLike | Date | null,
    endValue: DayjsLike | Date | string | null,
    timeValue: string | null,
  ) => void;
  getTimePickerFromFrequency: (frequency: FrequencyType) => string;
}

export default function FundraiserInformation({
  formData,
  setFormData,
  merchant,
  invalidFields,
  setInvalidFields,
  handleFieldFocus,
  isEditMode,
  isDrawerOpen,
  // Props from parent for state persistence across navigation
  paymentType,
  setPaymentType,
  startValues,
  setStartValue,
  endValues,
  setEndValue,
  timeValues,
  setTimeValue,
  handleFundChange,
  calculateTotalSubscriptionCharge,
  getTimePickerFromFrequency,
}: FundraiserInformationProps): React.ReactElement {
  // Local state only for data that doesn't need to persist across navigation
  const [possibleForms, setPossibleForms] = useState<FundraiserData[]>([]);
  const [isFormDataLoaded, setIsFormDataLoaded] = useState<boolean>(false);

  // Populate possibleForms from formToFormDataMap when drawer opens
  useEffect(() => {
    if (!isDrawerOpen) return;

    if (formData?.formToFormDataMap && Object.keys(formData.formToFormDataMap).length > 0) {
      const formsArray = Object.values(formData.formToFormDataMap);

      if (formsArray.length > 0) {
        setPossibleForms(formsArray);
        setIsFormDataLoaded(true);
      }
    }
  }, [formData?.formToFormDataMap, isDrawerOpen]);

  // Reset local states when drawer closes (only for local state, not parent-managed state)
  useEffect(() => {
    if (!isDrawerOpen) {
      setPossibleForms([]);
      setIsFormDataLoaded(false);
    }
  }, [isDrawerOpen]);

  // Ensure there's always at least one fund
  useEffect(() => {
    if (!formData.funds || formData.funds.length === 0) {
      setFormData((prev) => ({
        ...prev,
        funds: [
          {
            amount: '',
            fundraiser: '',
            fundName: '',
            formID: '',
            productGroup: '',
            fundraiserHasSponsershipGroups: false,
            product: '',
            customInputs: [],
            quantity: 1,
            frequency: 'One-Time',
            nextChargeDate: null,
            endChargeDate: null,
            chargeTime: null,
          },
        ],
      }));
    }
  }, [formData.funds, setFormData]);

  // Helper function to safely check if a field is invalid
  const isFieldInvalid = (fieldName: string): boolean => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      return invalidFields.has(fieldName);
    }
    return false;
  };

  function updateInvalidFieldsAfterRemoval(
    prev: Set<string>,
    removedIndex: number,
    prefix = 'fund-duplicate',
  ): Set<string> {
    const updated = new Set<string>();
    const target = `${prefix}-${removedIndex}`;
    const next = `${prefix}-${removedIndex + 1}`;

    const shouldRemove = prev.has(target) ? removedIndex : prev.has(next) ? removedIndex + 1 : null;

    for (const field of prev) {
      const match = field.match(new RegExp(`^${prefix}-(\\d+)$`));
      if (match) {
        const fieldIndex = parseInt(match[1], 10);

        if (fieldIndex === shouldRemove) {
          continue;
        }

        if (fieldIndex > removedIndex) {
          updated.add(`${prefix}-${fieldIndex - 1}`);
        } else {
          updated.add(field);
        }
      } else {
        updated.add(field);
      }
    }

    return updated;
  }

  // handleFundChange is now passed from parent

  const shouldDisableFundraiser = (fund: Fund): boolean => {
    return isEditMode && fund.fundraiserHasSponsershipGroups && paymentType === 'Online';
  };

  const shouldDisablePaymentType = () => {
    if (!isEditMode) return false;
    return paymentType === 'Online';
  };

  const shouldDisableOnlineOption = () => {
    if (!isEditMode) return false;
    return paymentType !== 'Online';
  };

  // Filter fundraising forms (exclude events)
  const possibleFundraisers = useMemo(() => {
    return possibleForms.filter((form) => form.formType !== 'EVENT');
  }, [possibleForms]);

  return (
    <div>
      {/* Payment Type */}
      <div className="mb-2">
        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
          Payment Type
        </Label>
        <select
          className={`block h-7 w-full rounded-md border-edge px-2 py-0 text-sm leading-tight focus:border-sage focus:outline-none focus:ring-sage ${
            shouldDisablePaymentType()
              ? 'cursor-not-allowed bg-paper-inset text-ink-muted'
              : 'bg-white'
          }`}
          value={paymentType}
          onChange={(e) => {
            setPaymentType(e.target.value);
            setFormData((prevData) => ({
              ...prevData,
              paymentType: e.target.value,
            }));
          }}
          onFocus={() => handleFieldFocus('paymentType')}
          disabled={shouldDisablePaymentType()}
        >
          <option value="Online" disabled={shouldDisableOnlineOption()}>
            Online
          </option>
          {OFFLINE_PAYMENT_METHODS.map((paymentMethod) => {
            const label = formatOfflinePaymentMethod(paymentMethod);
            return (
              <option key={paymentMethod} value={label}>
                {label}
              </option>
            );
          })}
        </select>
      </div>

      {/* Fundraiser Flow UI */}
      <div>
        <div className="flex flex-col gap-2">
          {formData?.funds?.map((fundItem, index) => (
            <div
              key={index}
              className={`relative space-y-2 rounded-lg border px-3 py-2
                    ${
                      invalidFields.has(`fund-duplicate-${index}`)
                        ? 'border-red-500'
                        : 'border-edge bg-paper-inset'
                    }
                    `}
            >
              {/* Form Selection */}
              <div className="grid grid-cols-1 gap-x-4 gap-y-2">
                <div>
                  <div className="flex items-center justify-between pb-0">
                    <label
                      htmlFor={`fundraiser-select-${index}`}
                      className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                    >
                      Fundraiser
                    </label>

                    {/* Only show remove button if not the first designation */}
                    {index > 0 && (
                      <button
                        onClick={() => {
                          setFormData((prevData) => ({
                            ...prevData,
                            funds: prevData.funds.filter((_, i) => i !== index),
                          }));
                          setInvalidFields((prev) => updateInvalidFieldsAfterRemoval(prev, index));
                        }}
                        className={`pb-1 pr-0 text-ink-muted  ${
                          isEditMode ? 'cursor-not-allowed' : 'hover:text-ink-secondary'
                        }`}
                        disabled={isEditMode}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 6L6 18" />
                          <path d="M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <select
                    id={`fundraiser-select-${index}`}
                    className={`
                        block h-7 w-full shrink-0 rounded-md px-2 py-0 text-sm leading-tight
                        ${
                          invalidFields.has(`fund-fundraiser-${index}`)
                            ? 'border-red-500'
                            : 'border-edge bg-white'
                        }
                        ${
                          shouldDisableFundraiser(fundItem) || !isFormDataLoaded
                            ? 'cursor-not-allowed bg-paper-inset text-ink-muted'
                            : ''
                        }
                    `}
                    value={fundItem.fundraiser || ''}
                    onChange={(e) => handleFundChange(index, 'fundraiser', e.target.value)}
                    onFocus={() => handleFieldFocus(`fund-fundraiser-${index}`)}
                    disabled={shouldDisableFundraiser(fundItem) || !isFormDataLoaded}
                  >
                    <option value="" disabled>
                      Select a fundraiser
                    </option>

                    {Array.isArray(possibleFundraisers) && possibleFundraisers.length > 0 ? (
                      possibleFundraisers
                        .sort((a, b) => a.formName.localeCompare(b.formName))
                        .map((obj) => {
                          const objFundraiserData = formData?.formToFormDataMap?.[obj.formName];
                          const hasProductGroups =
                            objFundraiserData?.fundraiserType === 'PRODUCT' &&
                            !!getProductGroupsForFrequency(
                              objFundraiserData,
                              fundItem.frequency || 'One-Time',
                            )?.length;

                          const isDisabled =
                            isEditMode && hasProductGroups && paymentType === 'Online';

                          return (
                            <option
                              key={obj.formID}
                              value={obj.formName}
                              disabled={isDisabled}
                              className={isDisabled ? 'text-ink-muted' : ''}
                            >
                              {obj.formName}
                            </option>
                          );
                        })
                    ) : (
                      <option value="">
                        {!isFormDataLoaded ? 'Loading form data...' : 'Loading forms...'}
                      </option>
                    )}
                  </select>
                </div>

                {/* Fund Selection */}
                <div>
                  <label
                    htmlFor={`fund-name-select-${index}`}
                    className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                  >
                    Fund
                  </label>
                  <select
                    id={`fund-name-select-${index}`}
                    className={`
                        block h-7 w-full shrink-0 rounded-md px-2 py-0 text-sm leading-tight
                        ${
                          invalidFields.has(`fund-name-${index}`)
                            ? 'border-red-500'
                            : 'border-edge bg-white'
                        }
                    `}
                    value={fundItem.fundName || ''}
                    onChange={(e) => handleFundChange(index, 'fundName', e.target.value)}
                    onFocus={() => handleFieldFocus(`fund-name-${index}`)}
                  >
                    {/* Always show current fund as first option */}
                    {fundItem.fundName && (
                      <option value={fundItem.fundName}>{fundItem.fundName}</option>
                    )}
                    <option value="" disabled>
                      Select a fund
                    </option>
                    {Array.isArray(formData?.formToFormDataMap?.[fundItem?.fundraiser]?.funds) ? (
                      (formData?.formToFormDataMap?.[fundItem?.fundraiser]?.funds ?? [])
                        .filter((fundName) => fundName !== fundItem?.fundName)
                        .sort((a, b) => a.localeCompare(b))
                        .map((fundName, idx) => (
                          <option key={idx} value={fundName}>
                            {fundName}
                          </option>
                        ))
                    ) : (
                      <option value="">Please select a fundraiser</option>
                    )}
                  </select>
                </div>

                {/* Sponsership Group Selection */}
                {(() => {
                  const fundraiserData = formData?.formToFormDataMap?.[fundItem?.fundraiser];
                  const effectiveGroups = getProductGroupsForFrequency(
                    fundraiserData,
                    fundItem.frequency || 'One-Time',
                  );
                  if (
                    fundraiserData?.fundraiserType !== 'PRODUCT' ||
                    !Array.isArray(effectiveGroups)
                  )
                    return null;
                  return (
                    <div>
                      <label
                        htmlFor={`fund-product-group-select-${index}`}
                        className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                      >
                        Sponsorship Group
                      </label>
                      <select
                        id={`fund-product-group-select-${index}`}
                        className={`
                            block h-7 w-full shrink-0 rounded-md px-2 py-0 text-sm leading-tight
                            ${
                              invalidFields.has(`fund-product-group-${index}`)
                                ? 'border-red-500'
                                : 'border-edge bg-white'
                            }
                            ${
                              isEditMode && paymentType === 'Online'
                                ? 'cursor-not-allowed bg-paper-inset text-ink-muted'
                                : ''
                            }
                            `}
                        value={fundItem.productGroup || ''}
                        onChange={(e) => handleFundChange(index, 'productGroup', e.target.value)}
                        onFocus={() => handleFieldFocus(`fund-product-group-${index}`)}
                        disabled={isEditMode && paymentType === 'Online'}
                      >
                        <option value="" disabled>
                          Select Sponsorship Group
                        </option>
                        {effectiveGroups
                          .filter((group) => group.productGroupName !== fundItem?.fundName)
                          .map((group, idx) => (
                            <option key={idx} value={group.productGroupName}>
                              {group.productGroupName}
                            </option>
                          ))}
                      </select>
                    </div>
                  );
                })()}

                {/* Sponsership Option Selection */}
                {formData?.formToFormDataMap?.[fundItem?.fundraiser]?.fundraiserType ===
                  'PRODUCT' &&
                  (() => {
                    const fundraiserData = formData?.formToFormDataMap?.[fundItem?.fundraiser];
                    const effectiveGroups = getProductGroupsForFrequency(
                      fundraiserData,
                      fundItem.frequency || 'One-Time',
                    );
                    if (!effectiveGroups) return null;

                    const selectedGroup = effectiveGroups.find(
                      (group) => group.productGroupName === fundItem?.productGroup,
                    );

                    const products = selectedGroup?.products;
                    const isDisabled = !Array.isArray(products) || products.length === 0;

                    return (
                      <div>
                        <label
                          htmlFor={`fund-product-select-${index}`}
                          className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                        >
                          Sponsorship Option
                        </label>
                        <select
                          id={`fund-product-select-${index}`}
                          className={`
                              block h-7 w-full shrink-0 rounded-md px-2 py-0 text-sm leading-tight
                              ${
                                invalidFields.has(`fund-product-${index}`)
                                  ? 'border-red-500'
                                  : 'border-edge'
                              }
                              ${
                                (isEditMode && paymentType === 'Online') || isDisabled
                                  ? 'cursor-not-allowed bg-paper-inset text-ink-muted'
                                  : 'bg-white'
                              }
                            `}
                          disabled={(isEditMode && paymentType === 'Online') || isDisabled}
                          value={fundItem.product ? JSON.stringify(fundItem.product) : ''}
                          onChange={(e) => {
                            const selectedValue = JSON.parse(e.target.value);
                            handleFundChange(index, 'product', selectedValue);
                            if (selectedValue && selectedValue.productPrice) {
                              handleFundChange(
                                index,
                                'amount',
                                selectedValue.productPrice.toString(),
                              );
                            }
                          }}
                          onFocus={() => handleFieldFocus(`fund-product-${index}`)}
                        >
                          <option value="" disabled>
                            Select Sponsorship Option
                          </option>
                          {Array.isArray(products) &&
                            products.map((product, idx) => (
                              <option
                                key={idx}
                                value={JSON.stringify({
                                  productID: product.productID,
                                  productName: product.productName,
                                  productPrice: product.productPrice,
                                })}
                              >
                                {product.productName}
                              </option>
                            ))}
                        </select>
                      </div>
                    );
                  })()}

                {/* Amount Input */}
                {fundItem.fundName &&
                  (!fundItem.fundraiserHasSponsershipGroups ||
                    (typeof fundItem.product === 'object' && fundItem.product?.productPrice)) && (
                    <div>
                      <label
                        htmlFor={`fund-amount-${index}`}
                        className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                      >
                        Amount
                      </label>
                      <div className="relative">
                        {/* Left $ label */}
                        <span
                          className={`absolute left-[10px] top-1/2 -translate-y-1/2 text-sm leading-none ${
                            fundItem.fundraiserHasSponsershipGroups
                              ? 'cursor-not-allowed text-ink-muted'
                              : 'text-ink-muted'
                          }`}
                        >
                          $
                        </span>

                        {/* Input field */}
                        <input
                          id={`fund-amount-${index}`}
                          name={`fund-amount-${index}`}
                          type="text"
                          value={fundItem.amount || ''}
                          disabled={
                            (isEditMode && paymentType === 'Online') ||
                            fundItem.fundraiserHasSponsershipGroups
                          }
                          onChange={(e) => handleFundChange(index, 'amount', e.target.value)}
                          onFocus={() => handleFieldFocus(`fund-amount-${index}`)}
                          className={`block h-7 w-full rounded-md pl-5 pr-12 text-sm leading-none focus:border-edge focus:outline-none focus:ring-0 ${
                            invalidFields.has(`fund-amount-${index}`)
                              ? 'border-red-500'
                              : 'border-edge bg-white'
                          } ${
                            (isEditMode && paymentType === 'Online') ||
                            fundItem.fundraiserHasSponsershipGroups
                              ? 'cursor-not-allowed bg-white text-ink-muted'
                              : 'bg-white'
                          }`}
                          placeholder="0.00"
                        />

                        {/* Right USD label */}
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-muted">
                          USD
                        </span>
                      </div>
                    </div>
                  )}

                {/* Quantity Input */}
                {fundItem.fundName &&
                  (!fundItem.fundraiserHasSponsershipGroups ||
                    (typeof fundItem.product === 'object' && fundItem.product?.productPrice)) &&
                  fundItem.amount && (
                    <div>
                      <label
                        htmlFor={`fund-quantity-${index}`}
                        className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                      >
                        Quantity
                      </label>
                      <div className="relative">
                        <input
                          id={`fund-quantity-${index}`}
                          name={`fund-quantity-${index}`}
                          type="number"
                          value={fundItem.quantity || 1}
                          min={1}
                          disabled={isEditMode && paymentType === 'Online'}
                          onChange={(e) => handleFundChange(index, 'quantity', e.target.value)}
                          onFocus={() => handleFieldFocus(`fund-quantity-${index}`)}
                          className={`block h-7 w-full rounded-md pl-3 pr-2 text-sm leading-none focus:border-edge focus:outline-none focus:ring-0 ${
                            invalidFields.has(`fund-quantity-${index}`)
                              ? 'border-red-500'
                              : 'border-edge bg-white'
                          } ${
                            isEditMode && paymentType === 'Online'
                              ? 'cursor-not-allowed bg-white text-ink-muted'
                              : ''
                          }`}
                          placeholder="1"
                        />
                      </div>
                    </div>
                  )}
              </div>
              {fundItem.fundName && fundItem.amount && fundItem.quantity && (
                <div className="mt-2 space-y-3">
                  {/* Frequency Selection */}
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Frequency
                    </Label>
                    <select
                      name={`fund-frequency-${index}`}
                      className={`
                                block h-7 w-full rounded-md border-edge bg-white px-2 py-0 text-sm leading-tight
                                focus:border-sage focus:outline-none focus:ring-sage
                                ${isFieldInvalid('frequency') ? 'border-red-500 ' : ''}
                                ${
                                  isEditMode && paymentType === 'Online'
                                    ? 'cursor-not-allowed bg-paper-inset text-ink-muted'
                                    : 'bg-white'
                                }
                            `}
                      value={fundItem.frequency || 'One-Time'}
                      onChange={(e) => handleFundChange(index, 'frequency', e.target.value)}
                      disabled={isEditMode && paymentType === 'Online'}
                      onFocus={() => handleFieldFocus(`fund-frequency-${index}`)}
                    >
                      <option value="One-Time">One-Time</option>
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Annually">Annually</option>
                    </select>
                  </div>

                  {/* Date Selection */}
                  {!isEditMode && fundItem.frequency !== 'One-Time' && paymentType === 'Online' && (
                    <div className="flex flex-col gap-2">
                      <div className="flex w-full py-2 text-edge">
                        <RecurringDatePicker
                          theme="payment_form"
                          fundKey={index}
                          startValue={
                            startValues[index]
                              ? dayjs(startValues[index])
                              : fundItem.nextChargeDate
                                ? dayjs(fundItem.nextChargeDate)
                                : null
                          }
                          setStartValue={setStartValue}
                          endValue={
                            endValues[index] === 'INFINITY'
                              ? 'INFINITY'
                              : endValues[index]
                                ? dayjs(endValues[index] as Date)
                                : fundItem.endChargeDate === 'INFINITY'
                                  ? 'INFINITY'
                                  : fundItem.endChargeDate
                                    ? dayjs(fundItem.endChargeDate as Date)
                                    : null
                          }
                          setEndValue={setEndValue}
                          timeValue={timeValues[index] ?? fundItem.chargeTime ?? null}
                          setTimeValue={setTimeValue}
                          lightColor={
                            isFieldInvalid(`fund-next-charge-date-${index}`)
                              ? '#ef4444'
                              : 'paper-muted'
                          }
                          darkColor="ink"
                          timePicker={getTimePickerFromFrequency(fundItem.frequency)}
                          calculateTotalSubscriptionCharge={calculateTotalSubscriptionCharge}
                          timezone={getEffectiveTimezone(merchant)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Form-level Custom Inputs Section */}
              {fundItem.customInputs && fundItem.customInputs.length > 0 && (
                <div className="mt-4 space-y-3">
                  {fundItem.customInputs.map((input, inputIndex) => (
                    <div key={inputIndex} className="space-y-0">
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        {input.title?.trim?.()}
                        {input.required && <span className="ml-1 text-red-500">*</span>}
                        {!input.required && <span className="ml-1 text-ink-muted">(optional)</span>}
                      </Label>

                      {input.type === 'SINGLE_SELECTION_DROPDOWN' && (
                        <select
                          value={fundItem.customInputs[inputIndex]?.value || ''}
                          onChange={(e) => {
                            setFormData((prevData) => ({
                              ...prevData,
                              funds: prevData.funds.map((fund, fundIndex) =>
                                fundIndex === index
                                  ? {
                                      ...fund,
                                      customInputs: fund.customInputs.map((ci, idx) =>
                                        idx === inputIndex
                                          ? {
                                              ...ci,
                                              value: e.target.value,
                                            }
                                          : ci,
                                      ),
                                    }
                                  : fund,
                              ),
                            }));
                          }}
                          className={`
                              block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                              ${
                                isFieldInvalid(`custom-input-${index}-${inputIndex}`)
                                  ? 'border-red-500'
                                  : 'border-edge bg-white'
                              }
                          `}
                          required={input.required}
                          onFocus={() => handleFieldFocus(`custom-input-${index}-${inputIndex}`)}
                        >
                          <option value="">Select an option</option>
                          {input.options?.map((option, optIdx) => (
                            <option key={optIdx} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      )}

                      {input.type === 'SHORT_TEXT_INPUT' && (
                        <input
                          type="text"
                          value={fundItem.customInputs[inputIndex]?.value || ''}
                          onChange={(e) => {
                            setFormData((prevData) => ({
                              ...prevData,
                              funds: prevData.funds.map((fund, fundIndex) =>
                                fundIndex === index
                                  ? {
                                      ...fund,
                                      customInputs: fund.customInputs.map((ci, idx) =>
                                        idx === inputIndex
                                          ? {
                                              ...ci,
                                              value: e.target.value,
                                            }
                                          : ci,
                                      ),
                                    }
                                  : fund,
                              ),
                            }));
                          }}
                          className={`
                              block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                              ${
                                isFieldInvalid(`custom-input-${index}-${inputIndex}`)
                                  ? 'border-red-500'
                                  : 'border-edge bg-white'
                              }
                          `}
                          placeholder="Enter your answer"
                          required={input.required}
                          onFocus={() => handleFieldFocus(`custom-input-${index}-${inputIndex}`)}
                        />
                      )}

                      {input.type === 'LONG_TEXT_INPUT' && (
                        <textarea
                          value={fundItem.customInputs[inputIndex]?.value || ''}
                          onChange={(e) => {
                            setFormData((prevData) => ({
                              ...prevData,
                              funds: prevData.funds.map((fund, fundIndex) =>
                                fundIndex === index
                                  ? {
                                      ...fund,
                                      customInputs: fund.customInputs.map((ci, idx) =>
                                        idx === inputIndex
                                          ? {
                                              ...ci,
                                              value: e.target.value,
                                            }
                                          : ci,
                                      ),
                                    }
                                  : fund,
                              ),
                            }));
                          }}
                          className={`
                                  block min-h-[100px] w-full shrink-0 rounded-md border px-2 py-1.5 text-sm leading-tight
                                  focus:border-sage focus:outline-none focus:ring-sage
                                  ${
                                    isFieldInvalid(`custom-input-${index}-${inputIndex}`)
                                      ? 'border-red-500'
                                      : 'border-edge bg-white'
                                  }
                              `}
                          rows={4}
                          placeholder="Enter your answer"
                          required={input.required}
                          onFocus={() => handleFieldFocus(`custom-input-${index}-${inputIndex}`)}
                        />
                      )}

                      {input.type === 'MULTI_SELECTION_DROPDOWN' && (
                        <MultiSelectDropdownNoCreate
                          height={28}
                          options={
                            input.options?.map((option) => ({
                              value: option,
                              label: option,
                            })) || []
                          }
                          items={(fundItem.customInputs[inputIndex]?.value || '')
                            .split(',')
                            .filter((v: string) => v.trim() !== '')}
                          setItems={(selectedValues: string[]) => {
                            setFormData((prevData) => ({
                              ...prevData,
                              funds: prevData.funds.map((fund, fundIndex) =>
                                fundIndex === index
                                  ? {
                                      ...fund,
                                      customInputs: fund.customInputs.map((ci, idx) =>
                                        idx === inputIndex
                                          ? {
                                              ...ci,
                                              value: selectedValues.join(','),
                                            }
                                          : ci,
                                      ),
                                    }
                                  : fund,
                              ),
                            }));
                          }}
                          error={isFieldInvalid(`custom-input-${index}-${inputIndex}`)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-2 flex w-full justify-between">
          {/* Add Fundraiser Button */}
          <button
            type="button"
            onClick={() => {
              setFormData((prevData) => ({
                ...prevData,
                funds: [
                  ...prevData.funds,
                  {
                    amount: '',
                    fundraiser: '',
                    fundName: '',
                    formID: '',
                    productGroup: '',
                    fundraiserHasSponsershipGroups: false,
                    product: '',
                    customInputs: [],
                    quantity: 1,
                    frequency: 'One-Time' as FrequencyType,
                    nextChargeDate: null,
                    endChargeDate: null,
                    chargeTime: null,
                  },
                ],
              }));
            }}
            className={`flex w-full items-center text-sm font-medium ${
              isEditMode
                ? 'cursor-not-allowed text-edge'
                : 'text-ink-muted hover:text-ink-secondary'
            }`}
            disabled={isEditMode}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-1"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add fundraiser
          </button>
        </div>
      </div>
    </div>
  );
}
